import React from "react";
import {
  Chart as ChartJS,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import * as S from "./cashFlowChart.style.ts";
import { CashFlowMonth } from "../../types.ts";
import { DARKER_GRAY } from "../../index.style.ts";

ChartJS.register(
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
);

interface CashFlowChartProps {
  projection: CashFlowMonth[];
}

const round = (n: number) => Math.round(n);

const CashFlowChart = ({ projection }: CashFlowChartProps) => {
  const labels = projection.map(
    (month) =>
      `${month.month.charAt(0).toUpperCase()}${month.month.slice(1, 3)} '${String(
        month.year,
      ).slice(2)}`,
  );

  const data = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label: "Money in (after tax)",
        data: projection.map((m) => round(m.inflow - m.taxReserved)),
        backgroundColor: "#A1EEBD",
        stack: "flow",
      },
      {
        type: "bar" as const,
        label: "Money out",
        data: projection.map(
          (m) =>
            -round(m.essentialOutflow + m.discretionaryOutflow + m.ownerDraw),
        ),
        backgroundColor: "#FFDBDB",
        stack: "flow",
      },
      {
        type: "line" as const,
        label: "Cash on hand (month end)",
        data: projection.map((m) => round(m.endingChecking + m.endingBuffer)),
        borderColor: DARKER_GRAY,
        backgroundColor: DARKER_GRAY,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        ticks: { callback: (v: number | string) => `$${v}` },
      },
    },
    plugins: {
      legend: { position: "bottom" as const, labels: { padding: 16 } },
      title: { display: true, text: "PROJECTED CASH FLOW" },
    },
  };

  return (
    <S.Wrapper>
      <Chart type="bar" data={data} options={options} />
    </S.Wrapper>
  );
};

export default CashFlowChart;
